import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiEye, mdiEyeOff } from '@mdi/js';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { changePassword } from '../../features/user/UserSlice';

const initialForm = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
};

const initialVisibility = {
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
};

const ChangePasswordDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState(initialForm);
    const [visibility, setVisibility] = useState(initialVisibility);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const validation = useMemo(() => {
        const errors = {};
        if (!form.currentPassword) {
            errors.currentPassword = 'Current password is required.';
        }
        if (!form.newPassword) {
            errors.newPassword = 'New password is required.';
        } else if (form.newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters.';
        }
        if (!form.confirmPassword) {
            errors.confirmPassword = 'Confirm the new password.';
        } else if (form.newPassword !== form.confirmPassword) {
            errors.confirmPassword = 'Passwords must match.';
        }
        return errors;
    }, [form]);

    const hasErrors = Object.keys(validation).length > 0;

    const resetForm = () => {
        setForm(initialForm);
        setVisibility(initialVisibility);
        setSubmitted(false);
    };

    const handleClose = () => {
        if (submitting) {
            return;
        }
        resetForm();
        onClose();
    };

    const handleChange = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const toggleVisibility = (field) => () => {
        setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const renderPasswordAdornment = (field, label) => (
        <InputAdornment position="end">
            <IconButton
                aria-label={visibility[field] ? `Hide ${label}` : `Show ${label}`}
                edge="end"
                onClick={toggleVisibility(field)}
                size="small"
            >
                <Icon path={visibility[field] ? mdiEyeOff : mdiEye} size={0.8} />
            </IconButton>
        </InputAdornment>
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        if (hasErrors || submitting) {
            return;
        }

        setSubmitting(true);
        try {
            await dispatch(changePassword({
                oldpassword: form.currentPassword,
                newpassword: form.newPassword,
                confirmpassword: form.confirmPassword,
            })).unwrap();
            toast.success('Password changed successfully');
            resetForm();
            onClose();
        } catch (error) {
            const message = typeof error === 'string' ? error : error?.error || error?.message || 'Unable to change password';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                    Change Password
                </Typography>
            </DialogTitle>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <TextField
                            label="Current Password"
                            name="currentPassword"
                            type={visibility.currentPassword ? 'text' : 'password'}
                            value={form.currentPassword}
                            onChange={handleChange('currentPassword')}
                            error={submitted && Boolean(validation.currentPassword)}
                            helperText={submitted ? validation.currentPassword || ' ' : ' '}
                            autoComplete="current-password"
                            fullWidth
                            disabled={submitting}
                            InputProps={{
                                endAdornment: renderPasswordAdornment('currentPassword', 'current password'),
                            }}
                        />
                        <TextField
                            label="New Password"
                            name="newPassword"
                            type={visibility.newPassword ? 'text' : 'password'}
                            value={form.newPassword}
                            onChange={handleChange('newPassword')}
                            error={submitted && Boolean(validation.newPassword)}
                            helperText={submitted ? validation.newPassword || ' ' : ' '}
                            autoComplete="new-password"
                            fullWidth
                            disabled={submitting}
                            InputProps={{
                                endAdornment: renderPasswordAdornment('newPassword', 'new password'),
                            }}
                        />
                        <TextField
                            label="Confirm New Password"
                            name="confirmPassword"
                            type={visibility.confirmPassword ? 'text' : 'password'}
                            value={form.confirmPassword}
                            onChange={handleChange('confirmPassword')}
                            error={submitted && Boolean(validation.confirmPassword)}
                            helperText={submitted ? validation.confirmPassword || ' ' : ' '}
                            autoComplete="new-password"
                            fullWidth
                            disabled={submitting}
                            InputProps={{
                                endAdornment: renderPasswordAdornment('confirmPassword', 'confirm password'),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting || hasErrors}
                    >
                        {submitting ? 'Changing...' : 'Change Password'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default ChangePasswordDialog;
